import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { documentosAPI } from '../services/api';
import { useIndividualPrecacheStatus } from '../contexts/IndividualPrecacheStatusContext';
import { toast } from 'sonner';

/**
 * Botón "🔄 Renderizar" con badge de progreso en vivo.
 *
 * Data source (dual):
 *  - Si está dentro de <IndividualPrecacheStatusProvider>: consume el context
 *    (1 polling global compartido → sin 429 aunque haya 100+ cards)
 *  - Si NO (ej: en el visor con 1 solo doc): hace polling individual al
 *    endpoint /progress-doc/{manualId} cada 5s (activo) o 30s (idle)
 *
 * Props:
 *   manualId (string, req): ID del manual
 *   docName (string, opc): nombre visible en toasts
 *   fileSizeBytes (number, opc): tamaño del PDF para confirmar precache >150MB
 *   variant: 'card' | 'viewer' — estilo del botón
 *   className: extra classes
 */
export default function PrecacheButtonWithBadge({
  manualId,
  docName = '',
  fileSizeBytes = 0,
  variant = 'card',
  className = '',
  alreadyRendered = false, // Si true, el botón queda DESHABILITADO con texto "Renderizado ✓"
}) {
  const [enqueuing, setEnqueuing] = useState(false);
  const ctx = useIndividualPrecacheStatus(); // null si estamos fuera del provider

  // ---- MODO CONTEXT: consumo estado compartido ----
  // ---- MODO INDIVIDUAL: polling propio (fallback para visor) ----
  const [individualProgress, setIndividualProgress] = useState(null);
  const pollTimerRef = useRef(null);

  const progressFromCtx = ctx && manualId ? ctx.getStatusForManual(manualId) : null;
  const progress = ctx ? progressFromCtx : individualProgress;

  const fetchIndividual = useCallback(async () => {
    if (!manualId) return;
    try {
      const res = await documentosAPI.getPrecacheProgressDoc(manualId);
      setIndividualProgress(res.data);
    } catch (err) {
      // silencioso
    }
  }, [manualId]);

  useEffect(() => {
    // Solo hacemos polling individual si NO estamos en el provider
    if (ctx) return;
    fetchIndividual();
    const schedule = () => {
      const isActive = individualProgress?.active === true;
      const delay = isActive ? 5000 : 30000;
      pollTimerRef.current = setTimeout(async () => {
        await fetchIndividual();
        schedule();
      }, delay);
    };
    schedule();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [ctx, manualId, individualProgress?.active, fetchIndividual]);

  const isActive = progress?.active === true;
  const status = progress?.status;
  const isFailed = status === 'failed' && !isActive;

  // MODO "Renderizado": si el PDF actual ya fue precacheado completo, el botón queda
  // deshabilitado con texto "Renderizado ✓". Es el estado más común post-render.
  // Si el admin sube una revisión nueva, alreadyRendered pasa a false (porque r2_file_key cambia).
  const isAlreadyRendered = alreadyRendered && !isActive && !isFailed;

  const handleClick = async () => {
    if (enqueuing || isActive || isAlreadyRendered) return;

    let forceLarge = false;
    const sizeMB = fileSizeBytes ? fileSizeBytes / (1024 * 1024) : 0;
    if (sizeMB > 150) {
      const ok = window.confirm(
        `El PDF de "${docName}" pesa ${Math.round(sizeMB)}MB. El renderizado va a consumir memoria y tomar un rato.\n\n¿Confirmás que querés precachear ahora?`
      );
      if (!ok) return;
      forceLarge = true;
    }

    setEnqueuing(true);
    try {
      const res = await documentosAPI.precachearIndividual(manualId, forceLarge);
      const data = res.data || {};
      if (data.pending_after_batch) {
        toast.info(`"${docName || 'Documento'}" en cola. Se procesará al terminar el batch masivo.`);
      } else {
        toast.success(`"${docName || 'Documento'}" en cola de renderizado. Te avisamos por la campanita al terminar.`);
      }
      // Forzar refresh inmediato del estado
      if (ctx) {
        ctx.triggerRefetch();
      } else {
        setTimeout(fetchIndividual, 500);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No se pudo encolar el renderizado.';
      toast.error(msg);
    } finally {
      setEnqueuing(false);
    }
  };

  // Estilos por variant
  const btnClasses = variant === 'viewer'
    ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors'
    : 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors';

  const enabledClasses = 'bg-teal-600 hover:bg-teal-700 text-white';
  const disabledClasses = 'bg-slate-200 text-slate-500 cursor-not-allowed';
  const failedClasses = 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300';
  const renderedClasses = 'bg-slate-100 text-slate-500 border border-slate-200 cursor-not-allowed';

  const disabled = enqueuing || isActive || isAlreadyRendered;
  const buttonClass = `${btnClasses} ${
    isAlreadyRendered ? renderedClasses :
    disabled ? disabledClasses :
    isFailed ? failedClasses :
    enabledClasses
  } ${className}`;

  const badgeText = (() => {
    if (!progress) return null;
    if (progress.status === 'pending') {
      return progress.queue_position ? `En cola · #${progress.queue_position}` : 'En cola';
    }
    if (progress.status === 'paused') return 'Pausado (batch masivo)';
    if (progress.status === 'processing_low') {
      const p = progress.total_pages ? ` · ${progress.current_page}/${progress.total_pages}` : '';
      return `Renderizando LOW${p} · ${progress.percent || 0}%`;
    }
    if (progress.status === 'processing_hd') {
      const p = progress.total_pages ? ` · ${progress.current_page}/${progress.total_pages}` : '';
      return `Renderizando HD${p} · ${progress.percent || 0}%`;
    }
    if (progress.status === 'processing_ocr') return 'Indexando búsqueda...';
    return null;
  })();

  return (
    <div className={variant === 'viewer' ? 'flex items-center gap-2' : 'flex flex-col items-end gap-1'}>
      <button
        onClick={handleClick}
        disabled={disabled}
        className={buttonClass}
        title={
          isAlreadyRendered ? 'Este documento ya está renderizado en HD. Si subís una nueva revisión, se habilitará automáticamente.' :
          isActive ? 'Ya está en cola/procesando' :
          isFailed ? `Falló: ${progress?.error || 'error'}. Click para reintentar.` :
          'Renderizar (HD + TOC + OCR)'
        }
        data-testid={`precache-btn-${manualId}`}
      >
        {enqueuing || isActive ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : isAlreadyRendered ? (
          <CheckCircle2 className="h-3.5 w-3.5" />
        ) : isFailed ? (
          <AlertTriangle className="h-3.5 w-3.5" />
        ) : (
          <RefreshCw className="h-3.5 w-3.5" />
        )}
        {isAlreadyRendered ? 'Renderizado' : 'Renderizar'}
      </button>

      {badgeText && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap"
          data-testid={`precache-badge-${manualId}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
          {badgeText}
        </span>
      )}

      {isFailed && !badgeText && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-700 border border-red-200"
          data-testid={`precache-badge-${manualId}-failed`}
          title={progress?.error || 'Error'}
        >
          <AlertTriangle className="h-2.5 w-2.5" />
          Falló · reintentar
        </span>
      )}

      {status === 'done' && !isActive && !isAlreadyRendered && variant === 'card' && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 border border-green-200"
          data-testid={`precache-badge-${manualId}-done`}
        >
          <CheckCircle2 className="h-2.5 w-2.5" />
          Renderizado
        </span>
      )}
    </div>
  );
}
